import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useAuthenticatedFetch } from '../../hooks/useAuth';
import { useTranslation } from '../../hooks/useTranslation';
import { parseStartIntent } from '../../core/seeds/startIntent';
import { DRAW_ORG_CHART_VIEW_ID } from '../../domains/orgchart/assistant/orgChartAssistantShared';
import { UpgradeModal } from '../../components/ui/UpgradeModal';
import { useUpgradeModalStore } from '../../stores/upgradeModalStore';
import { isPlanUpgradeError, parseApiErrorBody } from '../../domains/shared/utils/planUpgradeError';
import { OnboardingDialog } from '../../components/modals/OnboardingDialog';

interface CreateDiagramPageProps {
  inFolder?: boolean;
}

const PENDING_GENO_WIZARD_KEY = 'onboarding:pendingGenoWizard';
const PENDING_HIERARCHY_CHART_WIZARD_KEY = 'onboarding:pendingHierarchyChartWizard';

function readPendingGenoWizard(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage.getItem(PENDING_GENO_WIZARD_KEY);
}

function readPendingHierarchyChartWizard(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage.getItem(PENDING_HIERARCHY_CHART_WIZARD_KEY);
}

export const CreateDiagramPage: React.FC<CreateDiagramPageProps> = ({ inFolder = false }) => {
  const navigate = useNavigate();
  const params = useParams({ from: inFolder ? '/f/$folderId/d/new' : '/d/new' });
  const search = useSearch({ from: inFolder ? '/f/$folderId/d/new' : '/d/new' });
  const authenticatedFetch = useAuthenticatedFetch();
  const hasCreatedRef = useRef(false);
  const isDemoSignupTransitionRef = useRef(false);
  const { t } = useTranslation('common');
  const {
    isOpen: isUpgradeModalOpen,
    feature: upgradeFeature,
    reason: upgradeReason,
    closeModal: closeUpgradeModal,
    openModal: openUpgradeModal,
  } = useUpgradeModalStore();
  
  useEffect(() => {
    if (hasCreatedRef.current) return;

    // Check if user came through ?create=demo
    const paramsFromUrl = new URLSearchParams(window.location.search);
    const createMode = paramsFromUrl.get('create');
    const orgOptions = paramsFromUrl.get('orgOptions') === 'true';
    const hasGenoWizard = paramsFromUrl.has('genoWizard');
    const hasHierarchyChartWizard = paramsFromUrl.has('hierarchyChartWizard');
    const routeParams = params as Record<string, unknown>;
    const searchParams = search as Record<string, unknown>;
    const folderIdParam = typeof routeParams.folderId === 'string' ? routeParams.folderId : undefined;
    const searchString = (key: string): string | undefined => {
      const value = searchParams[key];
      return typeof value === 'string' ? value : undefined;
    };
    if (hasGenoWizard) {
      window.sessionStorage.setItem(PENDING_GENO_WIZARD_KEY, paramsFromUrl.get('genoWizard') ?? '');
    }
    if (hasHierarchyChartWizard) {
      window.sessionStorage.setItem(
        PENDING_HIERARCHY_CHART_WIZARD_KEY,
        paramsFromUrl.get('hierarchyChartWizard') ?? '',
      );
    }

    if (createMode === 'demo' && !inFolder) {
      const demoFolderId = localStorage.getItem('demoFolderId');
      if (demoFolderId) {
        hasCreatedRef.current = true;
        navigate({ to: `/f/${demoFolderId}/d/new?${paramsFromUrl.toString()}` });
        return;
      }
    }

    // Redirect only for dedicated test route instead of global bypass
    const urlTest = typeof window !== 'undefined' && window.location.search.includes('test=true');
    if (urlTest) {
      hasCreatedRef.current = true;
      const folderId = inFolder ? folderIdParam : 'test';
      const diagramId = `test`;
      const url = `/f/${folderId}/d/${diagramId}${urlTest ? '?test=true' : ''}`;
      navigate({ to: url });
      return;
    }

    // Guard: ensure we're actually on the expected route; otherwise skip
    try {
      if (inFolder) {
        if (!folderIdParam) return;
      }
    } catch {
      return;
    }
    const createDiagram = async () => {
      const name = searchString('name') || 'Blank';
      const type = searchString('type');
      const tag = searchString('tag');
      const templateId = searchString('templateId');
      const usr = searchString('usr');
      const onboardingMode = searchString('onboardingMode');
      const useCase = searchString('useCase');
      const startView = searchString('startView');
      const start = searchString('start');
      const dialect = searchString('dialect');
      const prompt = searchString('prompt');
      const sourcePromptKey = searchString('sourcePromptKey');
      const genoWizardParam = searchString('genoWizard');
      const genoWizard = genoWizardParam ?? readPendingGenoWizard();
      const hierarchyChartWizardParam = searchString('hierarchyChartWizard');
      const hierarchyChartWizard = hierarchyChartWizardParam ?? readPendingHierarchyChartWizard();
      const cloneFrom = searchString('cloneFrom');
      const folderId = inFolder ? folderIdParam : undefined;
      
      const inferredType = tag === 'ABCD-EFGG' ? 'org' : undefined;
      const diagramTypeParam = type || inferredType;
      
      const queryParams = new URLSearchParams({
        name,
        ...(diagramTypeParam ? { type: diagramTypeParam } : {}),
        ...(folderId && { folderId }),
        ...(templateId ? { templateId } : {}),
        ...(cloneFrom ? { cloneFrom } : {}),
        ...(usr ? { usr } : {}),
        ...(createMode === 'demo' && !folderId ? { scope: 'personal' } : {}),
      });
      
      try {
        hasCreatedRef.current = true;

        const response = await authenticatedFetch(`/api/diagrams/new?${queryParams}`);
        
        if (response.ok) {
          const data = await response.json() as { diagramId: string; redirect: string; type?: string };
          const diagramType = data.type || (typeof diagramTypeParam === 'string' ? diagramTypeParam : undefined);
          if (diagramType) {
            window.sessionStorage.setItem('diagramType', diagramType);
          }
          if (tag) {
            window.sessionStorage.setItem('diagramTag', tag);
          } else {
            window.sessionStorage.removeItem('diagramTag');
          }
          if (typeof window !== 'undefined') {
            const context: Record<string, unknown> = { usr: usr ?? null, templateId: templateId ?? null };
            if (onboardingMode === 'demo') context.loadDemoData = true;
            if (onboardingMode === 'org-options-sample') context.loadOrgOptionsSampleData = true;
            if (onboardingMode === 'genogram-blank-direct') {
              context.genogramStartMode = 'blank-direct';
            }
            if (onboardingMode === 'genogram-scenario') {
              context.genogramStartMode = 'scenario';
            }
            if (diagramType === 'network') {
              if (dialect) context.networkDialectId = dialect;
              if (start === 'import' || onboardingMode === 'csv') {
                context.networkStartPanel = 'import';
              } else if (start === 'resources') {
                context.networkStartPanel = 'resources';
              } else if (start === 'shapes' || start === 'blank' || start === 'template') {
                context.networkStartPanel = 'shapes';
              }
            }
            if (orgOptions) context.orgOptions = true;
            if (useCase) context.useCase = useCase;
            if (startView) context.startView = startView;
            if (prompt) context.assistantPrompt = prompt;
            if (sourcePromptKey) context.genogramSourcePromptKey = sourcePromptKey;

            // Canonical StartIntent (typed creation intent). Carried in the
            // onboarding blob so the editor can seed + decide wizard policy.
            const startIntent = parseStartIntent({
              ...(search as Record<string, unknown>),
              type: diagramType,
            });
            // Normalize the legacy network `start=` param into the typed
            // `panel` field — OnboardingContext derives the deprecated
            // `networkStartPanel` from it.
            if (!startIntent.panel && diagramType === 'network' && typeof start === 'string') {
              startIntent.panel =
                start === 'import' ? 'import' : start === 'resources' ? 'resources' : 'shapes';
            }
            context.startIntent = startIntent;
            if (genoWizard !== null) {
              context.genoWizard = genoWizard;
              window.sessionStorage.removeItem(PENDING_GENO_WIZARD_KEY);
            }
            if (hierarchyChartWizard !== null) {
              context.hierarchyChartWizard = hierarchyChartWizard;
              window.sessionStorage.removeItem(PENDING_HIERARCHY_CHART_WIZARD_KEY);
            }
            window.sessionStorage.setItem(`onboarding:${data.diagramId}`, JSON.stringify(context));
          }

          // Store demoFolderId only for the demo flow. Template starts are typed
          // by the backend so they do not reuse a folder from another domain.
          if (createMode === 'demo' && !localStorage.getItem('demoFolderId')) {
            // Extract folderId from the redirect URL and save in localStorage
            const match = data.redirect.match(/\/f\/([^/]+)\/d\//);
            if (match?.[1]) {
              const folderId = match[1];
              localStorage.setItem('demoFolderId', folderId);
            } 
          }

          const redirectUrl = new URL(data.redirect, window.location.origin);
          if (orgOptions) {
            redirectUrl.searchParams.set('orgOptions', 'true');
          }
          if (diagramType === 'org' && startView === 'manual') {
            redirectUrl.searchParams.set('review', DRAW_ORG_CHART_VIEW_ID);
          }
          navigate({
            to: `${redirectUrl.pathname}${redirectUrl.search}` ,
            replace: genoWizard !== null || hierarchyChartWizard !== null
          });
        } else {
          const error = parseApiErrorBody(
            await response.json().catch(() => null),
            t('errors.createDiagramFailed', { defaultValue: 'Failed to create diagram. Please try again.' }),
          );
          if (isPlanUpgradeError(error) && error.feature === 'demo-diagram') {
            openUpgradeModal('create-workspace', 'Upgrade to create more diagrams');
            return;
          }
          toast.error(
            t('errors.createDiagramFailed', { defaultValue: 'Failed to create diagram. Please try again.' }),
          );
          navigate({ to: '/' });
        }
      } catch (error) {
        console.error('Error creating diagram:', error);
        toast.error(
          t('errors.createDiagramFailed', { defaultValue: 'Failed to create diagram. Please try again.' }),
        );
        navigate({ to: '/' });
      }
    };
    
    createDiagram();
  }, [navigate, params, search, inFolder, authenticatedFetch, openUpgradeModal, t]);

  const handleUpgradeModalClose = () => {
    closeUpgradeModal();
    if (isDemoSignupTransitionRef.current) {
      return;
    }
    navigate({ to: '/' });
  };
  
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <div className="text-lg">
          {t('status.creatingDiagram', { defaultValue: 'Creating diagram...' })}
        </div>
      </div>
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={handleUpgradeModalClose}
        onUpgrade={() => {
          isDemoSignupTransitionRef.current = true;
        }}
        feature={upgradeFeature}
        reason={upgradeReason}
        page="create-workspace"
      />
      <OnboardingDialog externalOnly />
    </div>
  );
};
